export interface VendorCombination {
  billingCycle: 'monthly' | 'annual';
  term: number;
  label: string;
}

export interface VendorPreset {
  id: string;
  label: string;
  matchPatterns: string[];
  validCombinations: VendorCombination[];
  defaultBillingCycle: 'monthly' | 'annual';
  defaultTerm: number;
  /** When true, shows a "View plan details" button that opens a modal with plan inclusions */
  showPlanDetail?: boolean;
}

export const VENDOR_PRESETS: VendorPreset[] = [
  {
    id: 'microsoft',
    label: 'Microsoft',
    matchPatterns: ['microsoft'],
    validCombinations: [
      { billingCycle: 'monthly', term: 0, label: 'Month to Month' },
      { billingCycle: 'monthly', term: 12, label: 'Monthly — 1-Year Commitment' },
      { billingCycle: 'annual', term: 12, label: 'Annual — 1-Year Commitment' },
    ],
    defaultBillingCycle: 'monthly',
    defaultTerm: 12,
    showPlanDetail: true,
  },
];

export function getComboKey(billingCycle: string, term: number): string {
  return `${billingCycle}_${term}`;
}

const perUserPrice = (amount: number) =>
  `$${amount.toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/user`;

/**
 * List price for a product with term pricing: the month-to-month price marked "(M2M)", or the
 * headline price when the product isn't sold month to month.
 */
export function termPricedLabel(price: number, termPricing: Record<string, number>): string {
  const monthToMonth = termPricing[getComboKey('monthly', 0)];
  if (typeof monthToMonth === 'number' && monthToMonth > 0) return `${perUserPrice(monthToMonth)} (M2M)`;
  return price > 0 ? perUserPrice(price) : 'Contact for pricing';
}

export function detectVendorPreset(vendorOrBrand: string | undefined): VendorPreset | null {
  const lower = (vendorOrBrand || '').toLowerCase().trim();
  if (!lower) return null;
  return VENDOR_PRESETS.find(p =>
    p.matchPatterns.some(pat => lower === pat || lower.startsWith(pat))
  ) || null;
}

export function isValidCombo(preset: VendorPreset, billingCycle: string, term: number): boolean {
  return preset.validCombinations.some(c => c.billingCycle === billingCycle && c.term === term);
}

export function getValidTerms(preset: VendorPreset): number[] {
  return Array.from(new Set(preset.validCombinations.map(c => c.term)));
}

export function getValidBillingCycles(preset: VendorPreset): string[] {
  return Array.from(new Set(preset.validCombinations.map(c => c.billingCycle)));
}
